import clsx from 'clsx';
import { isNil } from 'lodash';
import { MouseEvent } from 'react';
import { internalAppPath } from '@shared/util/link';
import { getBrowserHistory, notifyNavigationListeners } from '@shared/util/navigation';
import { LinkProps } from './props';
import classNames from './Link.module.css';

/**
 * Where the link goes decides how it opens, and the anchor says so rather than leaving it to
 * whatever surface the link happens to be rendered in — a markdown link in a chat message, a
 * markdown link in an Ask answer, and a pasted URL token all arrive here.
 *
 * An external link always opens in a new tab — leaving Coast should never take the app with it.
 * An in-app link (see `internalAppPath`) stays in this tab and navigates through the router, so
 * the current view is replaced without a page load and anything mounted above the router — the
 * Ask panel, for one — survives the trip. A modifier-click reverses that: the href is a real
 * URL, so cmd-click (ctrl-click off Mac) gets the browser's own open-in-a-new-tab.
 */
export function Link({
  href,
  children = href,
  underline = true,
  style,
  stopPropagation = true,
}: LinkProps) {
  const inAppPath = internalAppPath(href);
  const isExternal = isNil(inAppPath);
  const browserHistory = getBrowserHistory();

  const handlePress = (event: MouseEvent<HTMLAnchorElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (isNil(inAppPath)) {
      return;
    }
    // Already handled by a surface that intercepts its own link clicks (see
    // `handleTranscriptLinkClick`) — or a modifier-click, which asks for a new tab/window
    // and gets the browser's own handling of the href.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    browserHistory.push(inAppPath);
    notifyNavigationListeners();
  };

  return (
    <a
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noreferrer noopener' : undefined}
      className={clsx(classNames.link, underline && classNames.underline)}
      style={style}
      onClick={handlePress}
    >
      {children}
    </a>
  );
}
